import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow,
  TableCell, TableBody, IconButton, Tooltip, Button, Alert, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Switch, FormControlLabel, Divider
} from '@mui/material';
import { Add, Edit, Delete, ArrowBack, Payment, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ACCT_TYPES = ['Bank Account', 'G/L Account', ''];

const emptyForm = { code: '', name: '', description: '', balancingAccountType: '', balancingAccountNo: '', isActive: true };

const PaymentMethodList = () => {
  const navigate = useNavigate();
  const [methods,    setMethods]    = useState([]);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [open,       setOpen]       = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open, value: form, onClose: () => setOpen(false) });
  const [saving,     setSaving]     = useState(false);
  const [formErr,    setFormErr]    = useState('');
  const [confirmDlg, setConfirmDlg] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/payables/payment-methods`, { headers: hdrs() });
      setMethods(data);
    } catch { setError('Failed to load payment methods'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setFormErr(''); setOpen(true); };
  const openEdit = (pm) => {
    setEditing(pm);
    setForm({
      code:                 pm.code,
      name:                 pm.name,
      description:          pm.description         || '',
      balancingAccountType: pm.balancingAccountType || '',
      balancingAccountNo:   pm.balancingAccountNo   || '',
      isActive:             pm.isActive !== false,
    });
    setFormErr(''); setOpen(true);
  };

  const handle = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!form.code.trim()) { setFormErr('Code is required'); return; }
    if (!form.name.trim()) { setFormErr('Name is required'); return; }
    setSaving(true); setFormErr('');
    try {
      if (editing) {
        await axios.put(`${API}/api/payables/payment-methods/${editing.code}`, form, { headers: hdrs() });
      } else {
        await axios.post(`${API}/api/payables/payment-methods`, form, { headers: hdrs() });
      }
      setOpen(false);
      setSuccess(editing ? 'Updated' : 'Created');
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (err) {
      setFormErr(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const confirmAction = async () => {
    const pm = confirmDlg;
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/payables/payment-methods/${pm.code}`, { headers: hdrs() });
      setSuccess('Deleted'); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>

          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Button startIcon={<ArrowBack />} size="small" onClick={() => navigate('/payables')}>Back</Button>
              <Payment sx={{ color: '#e65100' }} />
              <Typography variant="h5" fontWeight={700} sx={{ color: '#37474f' }}>Payment Methods</Typography>
              <Chip label={methods.length} size="small" variant="outlined" />
            </Box>
            <Button variant="contained" startIcon={<Add />} onClick={openAdd}
              sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}>
              Add Method
            </Button>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          <Divider sx={{ mb: 2 }} />

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Balancing Account Type</strong></TableCell>
                <TableCell><strong>Balancing Account No.</strong></TableCell>
                <TableCell align="center"><strong>Active</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {methods.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      No payment methods. Click "Add Method" or the system will seed defaults on first load.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : methods.map(pm => (
                <TableRow key={pm.code} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                  <TableCell>
                    <Chip label={pm.code} size="small" variant="outlined" color="warning"
                      sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{pm.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{pm.description || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    {pm.balancingAccountType
                      ? <Chip label={pm.balancingAccountType} size="small"
                          color={pm.balancingAccountType === 'Bank Account' ? 'info' : 'default'} />
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">{pm.balancingAccountNo || '—'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={pm.isActive ? 'Active' : 'Inactive'} size="small"
                      color={pm.isActive ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => openEdit(pm)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setConfirmDlg(pm)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

        </CardContent>
      </Card>

      {/* Confirm Delete Dialog */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Delete Payment Method
        </DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{confirmDlg?.name}</strong> ({confirmDlg?.code})?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog ── */}
      {discardDialog}
      <Dialog open={open} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit — ${editing.code}` : 'Add Payment Method'}</DialogTitle>
        <DialogContent dividers>
          {formErr && <Alert severity="error" sx={{ mb: 2 }}>{formErr}</Alert>}
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" gap={2}>
              <TextField label="Code *" name="code" value={form.code}
                onChange={handle} size="small" sx={{ width: 140 }}
                disabled={!!editing}
                helperText={editing ? 'Code cannot be changed' : 'e.g. CASH, BANK'}
                inputProps={{ style: { textTransform: 'uppercase' } }} />
              <TextField label="Name *" name="name" value={form.name}
                onChange={handle} size="small" fullWidth />
            </Box>
            <TextField label="Description" name="description" value={form.description}
              onChange={handle} size="small" fullWidth
              helperText="Shown as a tooltip on payment forms" />
            <TextField select label="Balancing Account Type" name="balancingAccountType"
              value={form.balancingAccountType} onChange={handle} size="small" fullWidth
              helperText="What type of account is automatically suggested when this method is used">
              <MenuItem value=""><em>— None —</em></MenuItem>
              {ACCT_TYPES.filter(Boolean).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            {form.balancingAccountType && (
              <TextField label="Default Balancing Account No." name="balancingAccountNo"
                value={form.balancingAccountNo} onChange={handle} size="small" fullWidth
                helperText={form.balancingAccountType === 'Bank Account'
                  ? 'Bank account No. to auto-select on payment forms'
                  : 'G/L account No. (e.g. cash-in-hand GL)'} />
            )}
            {editing && (
              <FormControlLabel
                control={<Switch name="isActive" checked={form.isActive}
                  onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
                label="Active" />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentMethodList;
